import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
  useRef,
} from "react";
import axiosInstance from "../api/axiosConfig";
import storage from "../utils/storage";
import { useAuth } from "./AuthContext";

// Services live on the server (GET/POST/PUT/DELETE /services). The server
// _id is the id; `id` is an alias added by the API.

const LEGACY_KEY = "services";

const errMsg = (err, fallback) => err?.error || err?.message || fallback;

// Forms edit price/taxRate as strings, so keep that shape for consumers.
const fromServer = (s) => ({
  ...s,
  id: s.id || s._id,
  price: s.price !== undefined && s.price !== null ? String(s.price) : "",
  taxRate: s.taxRate !== undefined && s.taxRate !== null ? String(s.taxRate) : "",
});

const toServer = (s) => {
  const payload = {
    name: (s.name || "").trim(),
    code: (s.code || "").trim(),
    description: s.description || "",
    price: Number(s.price) || 0,
    taxRate: Number(s.taxRate) || 0,
    taxAccount: s.taxAccount || "",
    isActive: s.isActive !== false,
  };
  if (s.unit !== undefined) payload.unit = s.unit;
  if (s.category !== undefined) payload.category = s.category;
  return payload;
};

const ServiceContext = createContext();

export const useService = () => useContext(ServiceContext);

export const ServiceProvider = ({ children }) => {
  const { user } = useAuth();
  const userId = user?._id || user?.id || null;
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const loadedFor = useRef(null);

  const fetchServices = useCallback(async () => {
    const res = await axiosInstance.get("/services", { params: { limit: 500 } });
    return (res.data?.data || []).map(fromServer);
  }, []);

  // One-time upload of services kept in localStorage by the old
  // implementation (matched by name, case-insensitive).
  const migrateLegacy = useCallback(async (serverServices) => {
    const legacy = storage.getJSON(LEGACY_KEY);
    if (!Array.isArray(legacy) || legacy.length === 0) {
      storage.removeItem(LEGACY_KEY);
      return false;
    }
    const names = new Set(serverServices.map((s) => s.name.toLowerCase()));
    let failed = false;
    let uploaded = false;
    for (const l of legacy) {
      if (!l.name || names.has(String(l.name).toLowerCase())) continue;
      try {
        await axiosInstance.post("/services", toServer(l));
        names.add(String(l.name).toLowerCase());
        uploaded = true;
      } catch (e) {
        failed = true;
      }
    }
    if (!failed) storage.removeItem(LEGACY_KEY);
    return uploaded;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let list = await fetchServices();
      if (await migrateLegacy(list)) list = await fetchServices();
      setServices(list);
    } catch (err) {
      setError(errMsg(err, "Failed to load services"));
    } finally {
      setLoading(false);
    }
  }, [fetchServices, migrateLegacy]);

  useEffect(() => {
    if (!userId) {
      loadedFor.current = null;
      setServices([]);
      return;
    }
    if (loadedFor.current === userId) return;
    loadedFor.current = userId;
    load();
  }, [userId, load]);

  // Mutators are async. add/update return the saved service and throw on
  // failure; delete/deactivate return { success, message }.
  const addService = async (service) => {
    try {
      const res = await axiosInstance.post("/services", toServer(service));
      const saved = fromServer(res.data.data);
      setServices((prev) => [...prev, saved]);
      return saved;
    } catch (err) {
      throw new Error(errMsg(err, "Failed to save service"));
    }
  };

  const updateService = async (id, updatedService) => {
    try {
      const res = await axiosInstance.put(
        `/services/${id}`,
        toServer(updatedService)
      );
      const saved = fromServer(res.data.data);
      setServices((prev) => prev.map((s) => (s.id === id ? saved : s)));
      return saved;
    } catch (err) {
      throw new Error(errMsg(err, "Failed to update service"));
    }
  };

  const deactivateService = async (id) => {
    try {
      const res = await axiosInstance.put(`/services/${id}`, { isActive: false });
      const saved = fromServer(res.data.data);
      setServices((prev) => prev.map((s) => (s.id === id ? saved : s)));
      return { success: true };
    } catch (err) {
      return { success: false, message: errMsg(err, "Failed to deactivate service") };
    }
  };

  const deleteService = async (id) => {
    try {
      await axiosInstance.delete(`/services/${id}`);
      setServices((prev) => prev.filter((s) => s.id !== id));
      return { success: true };
    } catch (err) {
      return { success: false, message: errMsg(err, "Failed to delete service") };
    }
  };

  const getService = useCallback(
    (id) => services.find((s) => s.id === id),
    [services]
  );
  const getActiveServices = useCallback(
    () => services.filter((s) => s.isActive),
    [services]
  );

  return (
    <ServiceContext.Provider
      value={{
        services,
        loading,
        error,
        reload: load,
        addService,
        updateService,
        deleteService,
        deactivateService,
        getService,
        getActiveServices,
      }}
    >
      {children}
    </ServiceContext.Provider>
  );
};

export default ServiceContext;
